import { ObjectUtils } from '../object';
import { SessionStoreKey } from './session-store-key.enum';
import { StorageConfiguration } from './StorageConfiguration.interface';
import { AuthCookie, LocalStore, SessionStore } from './storage.interface';
import { StringUtilities } from '../string/String.utilities';
import { CookieName } from './CookieName.definition';

export interface WindowWithStorage extends Window {
    DWSStorageInstance: StorageService;
}

export class StorageService {

    public static readonly identifier = '$storageService';

    /**
     * Get instance static method
     */
    public static getInstance(configuration: StorageConfiguration): StorageService {
        const windowInstance = this.getDOMInstance();
        if (windowInstance && windowInstance.getConfig().poolId === configuration.poolId) {
            return windowInstance;
        }

        if (!this.instance) {
            this.instance = new StorageService(configuration);
            (window as WindowWithStorage & typeof globalThis).DWSStorageInstance = this.instance;
        }

        return this.instance;
    }

    private static readonly key: string = 'DWS';

    private static instance: StorageService;

    private static getDOMInstance(): StorageService | undefined {
        const win: WindowWithStorage = window as WindowWithStorage & typeof globalThis;

        if (!win) {
            return undefined;
        }

        if (win.DWSStorageInstance === undefined) {
            return undefined;
        }

        return win.DWSStorageInstance;
    }

    private static parseStore<T>(data: string | null, storage: Storage, key: string): T {
        try {
            if (typeof data === 'string') {
                const parsedData = JSON.parse(StringUtilities.b64Decode(data)) as T;
                return parsedData;
            }
        } catch (error) {
            console.error('Error while parsing storage content. Setting default empty store');
            storage.setItem(key, StringUtilities.b64Encode(JSON.stringify({} as T)));
        }

        return {} as T;
    }

    public static setCookieAsExpired(name: CookieName): void {
        document.cookie = name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
    }

    private readonly localKey: string;

    private readonly localStorage: Storage = window.localStorage;

    private readonly sessionKey: string;

    private readonly sessionStorage: Storage = window.sessionStorage;

    private readonly configuration: StorageConfiguration;

    /**
     * Storage service ctor
     */
    constructor(configuration: StorageConfiguration) {
        this.configuration = configuration;
        this.localKey = `${ StorageService.key }-${ configuration.poolId }-lcst`;
        this.sessionKey = `${ StorageService.key }-${ configuration.poolId }-sest`;
    }

    public getConfig(): StorageConfiguration {
        return this.configuration;
    }

    public getUsername(): string | null {
        const data: AuthCookie | null = this.getCookieDataFor<AuthCookie>(CookieName.Authentication);

        return data !== null && data.username ? data.username : null;
    }

    public getRequestToken(): string | undefined {
        const session: SessionStore = ObjectUtils.deepClone(this.loadSession()) as SessionStore;
        if (!session) { return undefined; }

        const commonData = session[SessionStoreKey.Common];

        return commonData ? commonData.requestToken : undefined;
    }

    public setItem(key: string, value: string): void {
        this.localStorage.setItem(key, value);
    }

    public removeItem(key: string): void {
        this.localStorage.removeItem(key);
    }

    public getItem(key: string): string | null {
        const data = this.localStorage.getItem(key);
        if (!data) {
            return null;
        }

        return data;
    }

    public hasSetAuthCookie(): boolean {
        const data: AuthCookie | null = this.getCookieDataFor<AuthCookie>(CookieName.Authentication);

        return data !== null && data.authenticated === 1;
    }

    /**
     * Deletes local storage data for webshop instance
     */
    public deletePersistence(): void {
        return this.localStorage.removeItem(this.localKey);
    }

    /**
     * Deletes session storage data for webshop instance
     */
    public deleteSessionPersistence(): void {
        return this.sessionStorage.removeItem(this.sessionKey);
    }

    /**
     * Returns local storage data for webshop instance
     */
    public loadPersistence(): LocalStore | void {
        try {
            const content: string | null = this.localStorage.getItem(this.localKey);

            return StorageService.parseStore<LocalStore>(content, this.localStorage, this.localKey);
        } catch (error) {
            console.error('LocalStorage could not be loaded', error);
        }
    }

    /**
     * Updates local storage with provided data for webshop instance
     */
    public savePersistence(store: LocalStore): boolean {
        try {
            this.localStorage.setItem(this.localKey, StringUtilities.b64Encode(JSON.stringify(store)));
        } catch (error) {
            console.error('Data could not be stored to persistant store - error occured', error);
            return false;
        }

        return true;
    }

    /**
     * Deletes session storage data for webshop instance
     */
    public deleteSession(): void {
        return this.sessionStorage.removeItem(this.sessionKey);
    }

    /**
     * Returns session storage data for webshop instance
     */
    public loadSession(): SessionStore {
        const content: string = this.sessionStorage.getItem(this.sessionKey) || JSON.stringify({} as SessionStore);

        return StorageService.parseStore<SessionStore>(content, this.sessionStorage, this.sessionKey);
    }

    /**
     * Saves session storage with provided data for webshop instance
     */
    public saveSession(store: SessionStore): boolean {
        try {
            this.sessionStorage.setItem(this.sessionKey, StringUtilities.b64Encode(JSON.stringify(store)));
        } catch (error) {
            console.error('Data could not be stored to session store - error occured', error);
            return false;
        }

        return true;
    }

    public getCookieDataFor<T>(name: CookieName, decode = true): T | null {
        const result = new RegExp('(?:^|; )' + encodeURIComponent(name) + '=([^;]*)').exec(document.cookie);

        if (!result || result?.length === 0) {
            return null;
        }

        if (!!decode) {
            return JSON.parse(atob(decodeURIComponent(result[1]))) as T;
        }

        return result[1] as unknown as T;
    }

}
